import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';

import type {
  InvoicingBilling,
  InvoicingBillingPolicyTerm,
} from 'api/quote-marketplace';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

const termText = ({ years, months, days }: InvoicingBillingPolicyTerm) => {
  const parts = [
    years ? plural(years, 'year') : null,
    months ? plural(months, 'month') : null,
    days ? plural(days, 'day') : null,
  ].filter((p): p is string => Boolean(p));
  if (parts.length < 2) return parts[0] ?? null;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

const DOT_COLOR = {
  standard: 'green.500',
  non_standard: 'orange.400',
  multi_year: 'orange.400',
  invalid: 'red.500',
};

export const PolicyTermValue = ({ billing }: { billing: InvoicingBilling }) => {
  const muted = useColorModeValue('gray.500', 'gray.400');
  const term = billing.policy_term;

  const invalid = term.state === 'invalid';
  const note =
    term.state === 'standard'
      ? 'Standard term'
      : term.state === 'non_standard' && term.total_days !== null
        ? plural(term.total_days, 'day')
        : null;

  return (
    <Flex align="center" gap="2" wrap="wrap">
      <Box
        w="8px"
        h="8px"
        borderRadius="full"
        bg={DOT_COLOR[term.state]}
        flexShrink={0}
      />
      <Text as="span" color={invalid ? 'red.600' : undefined}>
        {invalid ? 'Check dates' : termText(term)}
      </Text>
      {note && (
        <Text as="span" fontSize="xs" color={muted}>
          {note}
        </Text>
      )}
    </Flex>
  );
};

export const MultiYearNotice = ({ billing }: { billing: InvoicingBilling }) => {
  const bg = useColorModeValue('orange.50', 'whiteAlpha.100');
  const border = useColorModeValue('orange.200', 'orange.300');
  if (billing.policy_term.state !== 'multi_year') return null;

  return (
    <Box
      mt="3"
      p="3"
      bg={bg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="md"
      fontSize="sm"
    >
      <Text fontWeight="700">Multi-Year Policy</Text>
      <Text>
        Multiyear policy coverages may be billed annually by the Insurer/MGA. If
        that is the case, only enter the first year here and handle the
        following years billing as if they are renewals.
      </Text>
    </Box>
  );
};
